<template>
  <v-card>
    <v-card-text class="padding-y-5 padding-x-6">
      <div class="d-flex justify-space-between align-center margin-b-3">
        <h2>
          Quote
        </h2>
        <div
          class="d-flex align-center background-gray-accent border-radius-12 padding-x-2 padding-y-1 margin-t-0 margin-r-0 font-medium font-14 h-24"
        >
          ID {{ quoteId }}
        </div>
      </div>
      <v-row dense class="font-16 margin-b-5">
        <v-col
          v-for="(charge, index) in charges"
          :key="`list-${charge.label}-${index}`"
          dense
          cols="12"
        >
          <v-row>
            <v-col
              cols="8"
              :class="{ 'text-primary': isDiscountChargeType(charge.amount) }"
            >
              {{ charge.label }}
            </v-col>
            <v-spacer />
            <v-col
              cols="auto"
              :class="{ 'text-primary': isDiscountChargeType(charge.amount) }"
            >
              {{ currencyFilter(charge.amount) }}
            </v-col>
          </v-row>
        </v-col>
        <v-col>
          <v-row>
            <v-col cols="8">
              <h2 class="font-16">
                {{ $t('quoteDetail.billingSummary.BOOKING_TOTAL') }}
                {{ showUsd ? ' (USD)' : '' }}
              </h2>
            </v-col>
            <v-spacer />
            <v-col cols="auto">
              <h2 class="font-16">
                {{ formattedTotal }}
              </h2>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <div v-if="formattedAmountDueEventually" class="margin-x-0 margin-b-5">
        <v-divider class="margin-b-5" />
        <v-row no-gutters>
          <v-col cols="8">
            <h2 class="font-16">
              {{
                alreadyBooked
                  ? $t('quoteDetail.billingSummary.REMAINING_BALANCE')
                  : $t('quoteDetail.billingSummary.DUE_NOW')
              }}
              {{ showUsd ? ' (USD)' : '' }}
            </h2>
          </v-col>
          <v-spacer />
          <v-col cols="auto">
            <h2 class="font-16 text-right">
              {{ formattedAmountDueNow }}
            </h2>
          </v-col>
        </v-row>
        <v-row class="margin-t-2" no-gutters justify="end">
          <template v-if="formattedAmountDueEventually">
            <v-col cols="8">
              {{ dueOnString }}
              {{ showUsd ? ' (USD)' : '' }}
            </v-col>
            <v-spacer />
            <v-col cols="auto">
              {{ formattedAmountDueEventually }}
            </v-col>
          </template>
        </v-row>
      </div>
      <v-btn
        v-if="button"
        id="billing-summary-button-checkout"
        block
        large
        depressed
        class="margin-t-5 margin-x-0 margin-b-6"
        color="primary"
        @click="$emit('click')"
      >
        <h2 class="text-white">
          {{ $t('quoteDetail.billingSummary.CONTINUE_TO_BOOKING') }}
        </h2>
      </v-btn>
      <v-divider class="margin-b-6" />
      <div class="d-flex">
        <CUIcon class="cursor-default margin-t-1" view-box="0 0 24 24" width="35px" height="35px">
          verified_user
        </CUIcon>
        <div class="margin-l-2 margin-t-1">
          <h4>{{ $t('bookingGuarantee.TITLE') }}</h4>
          <BookingProtectionDialog :is-self-serve="isSelfServe" />
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator'
import { DateTime } from 'luxon'
import { currencyFilter } from '@/utils/string'
import {
  Trip,
  AmenityType,
  CheckoutDetailResult,
  CheckoutDetailQuote,
} from '@/models/dto'
import BookingProtectionDialog from '@/components/BookingProtectionDialog.vue'
import { AmenityTypeId, ChargeTypeId, PaymentTypeKey } from "@/utils/enum"
import { TranslateResult } from 'vue-i18n'

@Component({ components: { BookingProtectionDialog } })
export default class BillingSummary extends Vue {
  @Prop({ type: Boolean }) readonly alreadyBooked: boolean
  @Prop({ type: Object, default: () => {} }) readonly checkoutDetail: CheckoutDetailResult
  @Prop({ type: Boolean }) readonly button: boolean
  @Prop({ type: Boolean, default: false }) readonly isElite: boolean

  currencyFilter = currencyFilter
  addedAmenities = [
    { id: AmenityTypeId.Wifi, label: 'Wifi' },
    { id: AmenityTypeId.LeatherSeats, label: 'Leather seats' },
    { id: AmenityTypeId.AlcoholConsumption, label: 'Alcohol allowed' },
  ]

  get formattedTotal(): string {
    return currencyFilter(this.checkoutDetail?.totalAmount)
  }

  get formattedAmountDueEventually(): string {
    const {amountDueEventually} = this.checkoutDetail || {}
    if (!amountDueEventually) {
      return ''
    }
    return currencyFilter(amountDueEventually)
  }

  get formattedAmountDueNow(): string {
    return currencyFilter(this.checkoutDetail?.amountDueNow)
  }

  get amountDueNow(): number {
    return this.checkoutDetail?.amountDueNow
  }

  get baseFare(): number {
    return this.checkoutDetail.charges.find(
      ({ chargeType }) => chargeType.id === ChargeTypeId.BaseFare
    )?.amount
  }

  get processingFeeAmount(): number {
    return this.checkoutDetail.charges.find(
      ({ chargeType }) => chargeType.id === ChargeTypeId.ProcessingFee
    )?.amount
  }

  get discountAmount(): number {
    return this.checkoutDetail.charges.find(
      ({ chargeType }) => chargeType.id === ChargeTypeId.Discount
    )?.amount
  }

  get totalAmenityAmount(): number {
    return this.checkoutDetail.charges.find(
      ({ chargeType }) => chargeType.id === ChargeTypeId.Amenities
    )?.amount
  }

  get charges(): { label: TranslateResult; amount: number; notes?: string }[] {
    const charges: {
      label: TranslateResult
      amount: number
      notes?: string
    }[] = [
      {
        label: this.$t('quoteDetail.billingSummary.BASE_FARE'),
        notes: this.formattedVehiclesList,
        amount: this.baseFare,
      },
    ]
    if (this.totalAmenityAmount) {
      charges.push({
        label: this.amenityLabel,
        amount: this.totalAmenityAmount,
      })
    }
    if (this.processingFeeAmount) {
      charges.push({
        label: this.$t('quoteDetail.billingSummary.PROCESSING_FEE'),
        amount: this.processingFeeAmount,
      })
    }
    if (this.discountAmount) {
      charges.push({
        label: `${this.$t('quoteDetail.billingSummary.DISCOUNT')} ${
          this.formattedDiscountPercent
        }`,
        amount: this.discountAmount,
      })
    }
    return charges.filter((charge) => charge.amount !== 0)
  }

  get formattedVehiclesList(): string {
    return this.checkoutDetail.quote.trips[0]?.vehicles
      ?.map(({ quantity, vehicleType }) => `${quantity}x ${vehicleType?.label}`)
      .join(', ')
  }

  get formattedDiscountPercent(): string {
    return this.checkoutDetail.discountPercent
      ? `(${Math.abs(this.checkoutDetail.discountPercent)}%)`
      : ''
  }

  get quote(): CheckoutDetailQuote {
    return this.checkoutDetail?.quote
  }

  get trips(): Trip[] {
    return this.quote?.trips || []
  }

  get firstTrip(): Trip {
    return this.trips?.[0]
  }

  get amenities(): AmenityType[] {
    return this.firstTrip?.tripAmenities || null
  }

  get showUsd(): boolean {
    return this.firstTrip?.stops?.[0]?.address?.country === 'CA'
  }

  get amenityLabel(): string {
    const matchedLabels = this.addedAmenities
      .filter((addedAmenity) =>
        this.amenities?.some((amenity) => amenity.id === addedAmenity.id)
      )
      .map((addedAmenity) => addedAmenity.label)
      .join(', ')

    return matchedLabels
  }

  get quoteId(): number {
    return this.quote?.quoteId
  }

  get dueDate(): string {
    const then = DateTime.fromISO(
      this.quote?.trips?.[0]?.dueDate
    ).toLocaleString({ month: 'long', day: 'numeric', year: 'numeric' })
    return then || ''
  }

  get dueOnString() {
    if (this.checkoutDetail?.paymentTypeKey === PaymentTypeKey.BillAfterServices) {
      return this.checkoutDetail?.paymentTermDays
        ? `Due ${this.checkoutDetail?.paymentTermDays} Days from Invoice Date`
        : 'Due Later'
    }

    if (this.checkoutDetail?.paymentTermDays) {
      return this.dueDate ? `Due on ${this.dueDate} (Net ${this.checkoutDetail?.paymentTermDays}) ` : 'Due Later'
    }

    return this.dueDate ? `Due on ${this.dueDate}` : 'Due Later'
  }

  get isSelfServe(): boolean {
    return !!this.quote?.isSelfServe
  }

  get startDate(): string {
    return this.quote?.trips?.[0]?.startDate
  }

  isDiscountChargeType(chargeAmount: number): boolean {
    return chargeAmount === this.discountAmount
  }
}
</script>
